import { useState, useEffect, useRef } from "react";
import "./InspectionRequest.scss";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { monthNames, weekDays } from "../../SourceData";
import Login from "../../pages/Login/Login";
import Map from "../../components/Map/Map"; // Import Map component
import AddIcon from "@mui/icons-material/AddBoxOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Address from "../../assets/images/address-icon.png";
import LocationIcon from "../../assets/images/location-icon.png";
import { toast } from "react-toastify"; // Add toast import

const InspectionRequest = ({
  packageType,
  listName,
  listDescription,
  onInspectionRequest,
  previewDesc,
  onClose,
  requiresAddress = false,
}) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1); // 1: Info, 2: Address, 3: Date
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Address selection state (similar to CartSummary)
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressOpen, setAddressOpen] = useState(false);
  const [openMap, setOpenMap] = useState(false);
  const addressRef = useRef(null);

  const { language, dir } = useLanguage();
  const { isAuthenticated } = useAuth();
  const isArabic = language === "ar";

  // Get user addresses
  const userAddresses = user?.address || [];
  const hasUserAddresses = userAddresses.length > 0;

  // Initialize selectedAddress from user addresses if available
  useEffect(() => {
    if (hasUserAddresses && userAddresses.length > 0) {
      if (!selectedAddress) {
        // If no address is selected, select the first one
        setSelectedAddress(userAddresses[0]);
      } else {
        // If an address is selected, make sure it still exists in the user's addresses
        const addressStillExists = userAddresses.some(
          (addr) => addr._id === selectedAddress._id
        );
        if (!addressStillExists) {
          // If the selected address no longer exists, select the first available address
          setSelectedAddress(userAddresses[0]);
        }
      }
    }
  }, [hasUserAddresses, selectedAddress, userAddresses]);

  // Reset selectedAddress if user has no addresses
  useEffect(() => {
    if (!hasUserAddresses && selectedAddress) {
      setSelectedAddress(null);
    }
  }, [hasUserAddresses, selectedAddress]);

  // Handle click away for address dropdown
  useEffect(() => {
    const handleClickAway = (event) => {
      if (addressRef.current && !addressRef.current.contains(event.target)) {
        setAddressOpen(false);
      }
    };

    if (addressOpen) {
      document.addEventListener("mousedown", handleClickAway);
    } else {
      document.removeEventListener("mousedown", handleClickAway);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickAway);
    };
  }, [addressOpen]);

  // Get package type message from backend description
  const getPackageTypeMessage = () => {
    if (listDescription) {
      return isArabic ? listDescription.ar : listDescription.en;
    }

    if (listName && listName.description) {
      return isArabic ? listName.description.ar : listName.description.en;
    }

    return isArabic
      ? "عزيزي العميل، هذا النوع من الاشتراكات يحتاج إلى معاينة مسبقة لتحديد السعر المناسب."
      : "Dear customer, this type of subscription requires a preliminary inspection to determine the appropriate price.";
  };

  // Get package type name
  const getPackageTypeName = () => {
    if (listName && listName.nameEn && listName.nameAr) {
      return isArabic ? listName.nameAr : listName.nameEn;
    }

    if (listName && listName.name) {
      return isArabic ? listName.name.ar : listName.name.en;
    }

    return packageType;
  };

  // Format user address (same as CartSummary)
  const formatUserAddress = (addressObj) => {
    if (!addressObj) return "";

    if (language === "ar") {
      return `${addressObj.city || ""}، ${addressObj.region || ""}، شارع ${
        addressObj.street || ""
      }، مبنى ${addressObj.buildingNo || ""}، طابق ${addressObj.floorNo || ""}`;
    } else {
      return `${addressObj.city || ""}, ${addressObj.region || ""}, ${
        addressObj.street || ""
      } St., Building ${addressObj.buildingNo || ""}, Floor ${
        addressObj.floorNo || ""
      }`;
    }
  };

  // Handle address selection
  const handleAddressSelection = (index) => {
    const addressData = userAddresses[index];
    setSelectedAddress(addressData);
    setAddressOpen(false);
  };

  // Handle address toggle
  const handleAddressToggle = () => {
    setAddressOpen(!addressOpen);
  };

  // Translations
  const translations = {
    requestInspection: isArabic ? "اطلب معاينة" : "Request Inspection",
    next: isArabic ? "التالي" : "Next",
    previous: isArabic ? "السابق" : "Previous",
    addressDetails: isArabic ? "تفاصيل العنوان" : "Address Details",
    selectDate: isArabic ? "اختر تاريخ المعاينة" : "Select Inspection Date",
    confirmRequest: isArabic
      ? "تأكيد طلب المعاينة"
      : "Confirm Inspection Request",
    cancel: isArabic ? "إلغاء" : "Cancel",
    back: isArabic ? "رجوع" : "Back",
    pleaseSelectDate: isArabic
      ? "يرجى اختيار تاريخ للمعاينة"
      : "Please select a date for inspection",
    loading: isArabic ? "جاري التحميل..." : "Loading...",
    noAddress: isArabic ? "لا يوجد عنوان محدد" : "No address selected",
    clickToAddAddress: isArabic
      ? " قم بالضغط لاضافة عنوان"
      : "Click to add address",
    noSavedAddresses: isArabic ? "لا توجد عناوين محفوظة" : "No saved addresses",
    addNewAddress: isArabic ? "أضف عنوان جديد" : "Add New Address",
  };

  // Check if we have a valid address (only selected address)
  const hasValidAddress = () => {
    return selectedAddress !== null;
  };

  // Generate calendar days
  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    let firstDayOfWeek = (firstDay.getDay() + 1) % 7;

    const days = [];

    // Add empty slots for days before first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }

    // Add days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      const isPastDate = date < today;

      days.push({
        day: i,
        date: date,
        isDisabled: isPastDate,
        isToday: date.toDateString() === today.toDateString(),
      });
    }

    setCalendarDays(days);
  }, [currentMonth]);

  // Calendar navigation
  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Handle date selection
  const handleDateClick = (day) => {
    if (day && !day.isDisabled) {
      setSelectedDate(day.date);
    }
  };

  // Handle step navigation
  const handleNext = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (currentStep === 1) {
      if (requiresAddress) {
        setCurrentStep(2);
      } else {
        setCurrentStep(3);
      }
    } else if (currentStep === 2 && hasValidAddress()) {
      setCurrentStep(3);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 3) {
      setCurrentStep(requiresAddress ? 2 : 1);
    } else if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  // Handle confirm inspection request
  const handleConfirmRequest = async () => {
    if (!selectedDate) return;

    setIsLoading(true);

    try {
      // Format date for API
      const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const formattedDate = formatDateForAPI(selectedDate);

      // Prepare address data with proper coordinates format
      let addressData = null;
      if (requiresAddress && selectedAddress) {
        // Use selected saved address only
        const { _id, ...addressDataWithoutId } = selectedAddress;

        addressData = {
          ...addressDataWithoutId,
          // Only include coordinates if they exist and are valid
          ...(selectedAddress.coordinates && {
            coordinates: Array.isArray(selectedAddress.coordinates)
              ? selectedAddress.coordinates
              : selectedAddress.coordinates.coordinates || null,
          }),
        };
      }

      // Prepare inspection data
      const inspectionData = {
        date: formattedDate,
        ...(requiresAddress && { address: addressData }),
      };

      // Call the parent callback with inspection request data
      if (onInspectionRequest) {
        await onInspectionRequest(inspectionData);
      }

      // Close the component
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Error requesting inspection:", error);

      // Better error handling with toast
      let errorMessage = isArabic
        ? "فشل في طلب المعاينة"
        : "Failed to request inspection";

      // Check for API response error message
      if (error.response && error.response.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Display error using toast
      toast.error(errorMessage);

      // Don't close the component on error, let user try again
    } finally {
      setIsLoading(false);
    }
  };

  // Handle login modal close
  const handleLoginClose = () => {
    setShowLoginModal(false);
  };

  // Format date for display
  const getFormattedDate = (date) => {
    if (!date) return "";
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString(isArabic ? "ar-SA" : "en-US", options);
  };

  const shouldShowPreviewDesc = () => {
    if (!previewDesc) return false;

    // Check if the object has the language properties and they're not empty
    const hasEnglish = previewDesc.en && previewDesc.en.trim() !== "";
    const hasArabic = previewDesc.ar && previewDesc.ar.trim() !== "";

    // Return true only if at least one language has content
    return hasEnglish || hasArabic;
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="inspection-info">
            <div className="package-type-header">
              <h3 className="package-type-title">{getPackageTypeName()}</h3>
            </div>

            <div className="inspection-message">
              <p>{getPackageTypeMessage()}</p>
            </div>

            {/* Conditionally render the inspection-note div */}
            {shouldShowPreviewDesc() && (
              <div className="inspection-note">
                <p className="note-text">
                  {isArabic ? previewDesc.ar : previewDesc.en}
                </p>
              </div>
            )}

            <button className="request-inspection-btn" onClick={handleNext}>
              {translations.requestInspection}
            </button>
          </div>
        );

      case 2:
        return (
          <div className="address-selection-view">
            <div className="step-header">
              <button className="back-btn" onClick={handlePrevious}>
                {translations.previous}
              </button>
              <h3 className="section-title">{translations.addressDetails}</h3>
            </div>

            {/* Address Selection (Similar to CartSummary) */}
            <div className="address-selector" onClick={handleAddressToggle}>
              <img src={Address} alt="" />
              {selectedAddress ? (
                <p className="selected-address-container">
                  <span className="selected-address-name">
                    {selectedAddress.name}
                  </span>
                  {formatUserAddress(selectedAddress)}
                </p>
              ) : (
                <p>
                  <span>{translations.noAddress}</span>
                  {translations.clickToAddAddress}
                </p>
              )}
              {dir === "rtl" ? (
                <ArrowDownIcon className="icon" />
              ) : (
                <ArrowDownIcon className="icon" />
              )}

              {addressOpen && (
                <div ref={addressRef} className="address-dropdown">
                  {/* Map through user addresses if available */}
                  {hasUserAddresses ? (
                    userAddresses.map((address, index) => (
                      <div
                        className={`address-item ${
                          selectedAddress && selectedAddress._id === address._id
                            ? "selected"
                            : ""
                        }`}
                        key={address._id || index}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddressSelection(index);
                        }}
                      >
                        <div className="img-container">
                          <img src={LocationIcon} alt="" />
                        </div>
                        <div className="text-container">
                          <p>{address.name}</p>
                          <span>{formatUserAddress(address)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    // No addresses message
                    <div className="no-address-message">
                      <p>{translations.noSavedAddresses}</p>
                    </div>
                  )}

                  <div
                    className="address-header"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMap(true);
                    }}
                  >
                    <AddIcon />
                    <p className="add-address">{translations.addNewAddress}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="step-actions">
              <button
                className={`next-btn ${!hasValidAddress() ? "disabled" : ""}`}
                onClick={handleNext}
                disabled={!hasValidAddress()}
              >
                {translations.next}
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="date-selection-view">
            <div className="date-selection-header">
              <button className="back-btn" onClick={handlePrevious}>
                {translations.previous}
              </button>
              <h3 className="section-title">{translations.selectDate}</h3>
            </div>

            {/* Calendar */}
            <div
              className="custom-calendar"
              role="application"
              aria-label="Calendar"
            >
              <div className="calendar-header">
                <button
                  className="month-nav"
                  onClick={prevMonth}
                  aria-label={isArabic ? "الشهر السابق" : "Previous month"}
                >
                  &#9656;
                </button>
                <h4 className="current-month">
                  {monthNames[currentMonth.getMonth()]}{" "}
                  {currentMonth.getFullYear()}
                </h4>
                <button
                  className="month-nav"
                  onClick={nextMonth}
                  aria-label={isArabic ? "الشهر التالي" : "Next month"}
                >
                  &#9666;
                </button>
              </div>

              <div className="calendar-weekdays" role="row">
                {weekDays.map((day) => (
                  <div key={day} className="weekday" role="columnheader">
                    {day}
                  </div>
                ))}
              </div>

              <div className="calendar-days" role="grid">
                {calendarDays.map((day, index) => (
                  <div
                    key={index}
                    className={`calendar-day ${!day.day ? "empty" : ""} ${
                      day.isDisabled ? "disabled" : ""
                    } ${day.isToday ? "today" : ""} ${
                      selectedDate &&
                      day.date &&
                      day.date.toDateString() === selectedDate.toDateString()
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleDateClick(day)}
                    tabIndex={day.day && !day.isDisabled ? 0 : -1}
                    role={day.day ? "gridcell" : "presentation"}
                    aria-selected={
                      selectedDate &&
                      day.date &&
                      day.date.toDateString() === selectedDate.toDateString()
                    }
                    aria-disabled={day.isDisabled}
                  >
                    {day.day}
                  </div>
                ))}
              </div>
            </div>

            {/* Selected date confirmation */}
            {selectedDate && (
              <div className="selected-date-info">
                <p className="selected-date-text">
                  {isArabic
                    ? `تاريخ المعاينة المحدد: ${getFormattedDate(selectedDate)}`
                    : `Selected inspection date: ${getFormattedDate(
                        selectedDate
                      )}`}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="date-selection-actions">
              <button
                className={`confirm-inspection-btn ${
                  !selectedDate ? "disabled" : ""
                }`}
                onClick={handleConfirmRequest}
                disabled={!selectedDate || isLoading}
              >
                {isLoading ? translations.loading : translations.confirmRequest}
              </button>
            </div>

            {!selectedDate && (
              <div className="select-date-prompt">
                <p>{translations.pleaseSelectDate}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="inspection-request">
        {/* Step content */}
        {renderStepContent()}
      </div>

      {/* Map Modal */}
      <Map open={openMap} onClose={() => setOpenMap(false)} />

      {/* Login Modal */}
      {showLoginModal && (
        <Login open={showLoginModal} onClose={handleLoginClose} />
      )}
    </>
  );
};

export default InspectionRequest;